import { supabase } from "@/integrations/supabase/client";

/**
 * PQ-3b guest paywall events (canonical names, greppable from fire sites):
 * - guest_paywall_shown          — sheet opened (gate blocked a submit)
 * - guest_paywall_dismissed      — user closed without auth
 * - guest_paywall_signup_started — Apple / Google / email button tapped
 * - guest_paywall_signup_completed — auth completed inside paywall flow
 * Props: { guest_handle, search_count, auth_method? }
 *
 * PQ-5a verdict-card events:
 * - results_viewed — fires once per outfit view in PurchaseConfidenceCard
 *   after verdict resolves. Props:
 *     outfit_id: string
 *     confidence_score: number (0-100)
 *     confidence_level: "low" | "medium" | "medium-high" | "high"
 *     verdict: "buy" | "your_call" | "skip"
 *     is_guest: boolean
 *   guest_handle auto-attached by setGuestHandleForAnalytics enrichment.
 *
 * PQ-5e tier alternatives accordion:
 * - tier_alternatives_expanded — fires the first time the user taps
 *   "See alternatives" per PurchaseConfidenceCard mount (debounced via
 *   local ref; collapse + subsequent expands are not tracked). Suppressed
 *   on Skip verdicts (trigger never renders). Props:
 *     outfit_id: string | undefined
 *     verdict: "buy" | "your_call" | "skip"  // never "skip" in practice
 *     is_guest: boolean
 *     tier_count: number (1-3, count of available tiers after RETAILER-CAP-1)
 *
 * PQ-5f verdict-rendered telemetry:
 * - verdict_rendered — fires once per PurchaseConfidenceCard mount on first
 *   complete verdict + lens render (debounced via local ref). Captures the
 *   rendered lens state for post-hoc tone QA (e.g. sampling real Skip
 *   outputs against Brief 4 dignity rules). Props:
 *     outfit_id: string | undefined
 *     verdict: "buy" | "your_call" | "skip"
 *     confidence_score: number (0-100)
 *     tiers_fired: TierTag[]
 *     fit_lens_read: "++" | "+" | "neutral" | "-" | "--" | null
 *     fit_lens_copy: string | null
 *     suits_you_present: boolean
 *     pairs_with_read: "++" | "+" | "neutral" | "-" | "--" | null
 *     fills_gap_present: boolean
 *     is_guest: boolean
 *
 * PQ-4 guest repeat-use instrumentation:
 * - guest_session_resumed — fires once per cold load when an existing guest
 *   session resolves AND (search_count > 0 OR session age > 24h). Measures
 *   returning-guest %. Suppressed on the very first visit. Props:
 *     handle: string | null
 *     days_since_first_visit: number (integer days, floor)
 *     search_count: number
 * - guest_saved_viewed — fires once per SavedScreen mount (guest branch)
 *   after the saved list resolves, regardless of count. Props:
 *     handle: string | null
 *     saved_count: number
 *     is_empty: boolean
 * - guest_account_viewed — fires once per GuestAccount mount. Props:
 *     handle: string | null
 *     days_since_first_visit: number
 *     outfit_count: number
 *     saved_count: number
 * - guest_saved_signup_tapped — fires when guest taps the footer banner CTA
 *   on the Saved screen. Props: handle, saved_count.
 *
 * PHASE-0 email instrumentation:
 * - welcome_email_triggered — fires when the app invokes send-welcome-email
 *   after signup completion (cold signup OR guest claim). Edge function
 *   dedupes via profiles.welcome_sent_at, so this event fires even when
 *   the email is suppressed as a duplicate; use email_events table for
 *   actual send confirmations. Props: { user_id }.
 * - look_ready_email_triggered — NOT emitted from the app. The Look-Ready
 *   email is fired server-side by the outfits UPDATE → pg_net trigger when
 *   lookbook_status flips to 'complete'. Track sends via email_events
 *   (event_type='sent', email_kind='look_ready').
 */



// PQ-3c — module-level guest handle for analytics auto-attach.
// Set once after the guest_sessions RPC resolves in src/routes/index.tsx.
let _guestHandleForAnalytics: string | null = null;

export function setGuestHandleForAnalytics(handle: string | null): void {
  _guestHandleForAnalytics = handle;
}


/**
 * Fire-and-forget analytics tracker. Never throws, never blocks the UI.
 * Works for both signed-in users and guests (uses guest_session_id from
 * localStorage). Silently swallows any error.
 */
export function trackEvent(
  eventType: string,
  properties: Record<string, unknown> = {},
): void {
  void (async () => {
    try {
      const { data } = await supabase.auth.getUser();
      const user = data?.user ?? null;
      const guestSessionId =
        typeof window !== "undefined"
          ? localStorage.getItem("guest_session_id")
          : null;

      const enrichedProps =
        !user && _guestHandleForAnalytics && !("guest_handle" in properties)
          ? { ...properties, guest_handle: _guestHandleForAnalytics }
          : properties;

      await supabase.from("analytics_events").insert({
        user_id: user?.id ?? null,
        guest_session_id: user ? null : guestSessionId,
        event_type: eventType,
        properties: enrichedProps,
      } as never);
    } catch (err) {
      console.error("Analytics error:", err);
    }
  })();
}

/**
 * Push an event onto window.dataLayer for GTM (Pinterest, GA4, etc.).
 * Safe on SSR (no-op) and never throws. Use BEFORE navigating away so the
 * push isn't lost on redirect.
 */
export function pushDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(payload);
  } catch {
    /* ignore */
  }
}

const TRIGGER_KEY = "last_signup_trigger";

export function setSignupTrigger(trigger: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(TRIGGER_KEY, trigger);
  } catch {
    /* ignore */
  }
}

export function consumeSignupTrigger(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(TRIGGER_KEY);
    if (v) localStorage.removeItem(TRIGGER_KEY);
    return v;
  } catch {
    return null;
  }
}

/**
 * Read any UTM values previously stashed in sessionStorage by a campaign
 * landing page (e.g. /new-job, /get-the-look). Returns a flat object with
 * only the keys that have a non-empty value, suitable for spreading into a
 * dataLayer event payload. Safe on SSR, never throws.
 */
export function getStoredUtms(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
  const out: Record<string, string> = {};
  try {
    for (const k of keys) {
      const v = sessionStorage.getItem(`l2f_${k}`);
      if (v) out[k] = v;
    }
  } catch {
    /* ignore */
  }
  return out;
}
